import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Check, ArrowRight, BookOpen } from 'lucide-react';
import sixthLogo from '../assets/icon.svg';
import '../styles/sixthDesign.css';

const STEPS = [
  {
    id: 1,
    eyebrow: 'Step 01',
    title: 'Open VS Code',
    description: 'Your browser will ask permission to launch Visual Studio Code. Choose "Open Visual Studio Code" in the popup.',
    cta: 'Open VS Code',
    note: null,
  },
  {
    id: 2,
    eyebrow: 'Step 02',
    title: 'Authorize',
    description: 'Inside VS Code, a modal asks to "Open a URI". Click "Open" to connect this account to the Sixth extension.',
    cta: "I've clicked Open",
    note: "If you don't see the modal, open the Sixth sidebar by clicking the logo in the activity bar.",
  },
  {
    id: 3,
    eyebrow: 'Step 03',
    title: "You're ready",
    description: 'Connection established. The Sixth panel is now live in VS Code with all your account benefits, including frontier models and agent mode.',
    cta: 'Go to dashboard',
    note: null,
  },
];

/* ───────────── Mock visuals (pure HTML/CSS) ───────────── */

const BrowserPopup = () => (
  <div style={{
    position: 'relative',
    width: '100%', height: '100%',
    background: 'linear-gradient(180deg, #1a1a22 0%, #131319 100%)',
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  }}>
    {/* Browser chrome */}
    <div style={{
      height: 42, background: '#0d0d12',
      borderBottom: '1px solid var(--line)',
      display: 'flex', alignItems: 'center',
      padding: '0 14px', gap: 10,
    }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#ff5f57' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#febc2e' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#28c840' }} />
      </div>
      <div style={{
        flex: 1, marginLeft: 12,
        height: 26, borderRadius: 7,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', padding: '0 12px',
        fontSize: 11.5, color: 'var(--text-3)',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <span style={{ color: 'var(--success)', marginRight: 8, fontSize: 10 }}>⬢</span>
        app.trysixth.com/vscode/onboarding
      </div>
    </div>

    {/* Faux page bg + dialog */}
    <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(193,61,255,0.10), transparent 60%)',
        opacity: 0.4,
      }} />
      <div style={{
        position: 'absolute', inset: 16,
        opacity: 0.18,
        display: 'flex', flexDirection: 'column', gap: 10, padding: 30,
      }}>
        <div style={{ width: '50%', height: 14, background: 'var(--text)', borderRadius: 4 }} />
        <div style={{ width: '70%', height: 8, background: 'var(--text-2)', borderRadius: 4 }} />
        <div style={{ width: '60%', height: 8, background: 'var(--text-2)', borderRadius: 4 }} />
      </div>

      <div style={{
        position: 'relative',
        width: 'min(82%, 380px)',
        background: 'linear-gradient(180deg, #25252e, #1c1c24)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 12,
        boxShadow: '0 30px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(193,61,255,0.18), 0 0 40px rgba(193,61,255,0.18)',
        padding: 22,
        zIndex: 2,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'linear-gradient(135deg, #007ACC 0%, #005f9e 100%)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, color: 'white', fontWeight: 700, fontFamily: 'serif',
          }}>V</div>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>Open Visual Studio Code?</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>app.trysixth.com wants to open this application</div>
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: 'var(--text-3)',
          padding: '8px 10px', background: 'rgba(255,255,255,0.03)',
          borderRadius: 7, marginBottom: 14,
        }}>
          <input type="checkbox" defaultChecked readOnly style={{ accentColor: 'var(--accent)' }} />
          Always allow app.trysixth.com to open links of this type
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
            background: 'transparent', color: 'var(--text-2)',
            border: '1px solid var(--line-2)',
            fontFamily: 'inherit', cursor: 'default',
          }}>Cancel</button>
          <button type="button" style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: 'var(--accent)', color: 'white',
            border: 'none',
            boxShadow: '0 0 0 2px rgba(193,61,255,0.3), 0 8px 16px rgba(193,61,255,0.4)',
            fontFamily: 'inherit', cursor: 'default',
            animation: 'sd-onb-pulse 2s ease-in-out infinite',
          }}>Open Visual Studio Code</button>
        </div>
      </div>
    </div>
    <style>{`
      @keyframes sd-onb-pulse {
        0%, 100% { transform: translateY(0); box-shadow: 0 0 0 2px rgba(193,61,255,0.3), 0 8px 16px rgba(193,61,255,0.4); }
        50%      { transform: translateY(-1px); box-shadow: 0 0 0 4px rgba(193,61,255,0.2), 0 12px 24px rgba(193,61,255,0.5); }
      }
    `}</style>
  </div>
);

const VSCodeWindow = ({ stage, email }) => (
  <div style={{
    position: 'relative',
    width: '100%', height: '100%',
    background: '#1e1e1e',
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  }}>
    {/* Title bar */}
    <div style={{
      height: 32, background: '#323233',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 12px',
      fontSize: 11, color: 'rgba(255,255,255,0.65)',
    }}>
      <div style={{ display: 'flex', gap: 5 }}>
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#ff5f57' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#febc2e' }} />
        <span style={{ width: 11, height: 11, borderRadius: 999, background: '#28c840' }} />
      </div>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>sixth-ai — VS Code</span>
      <span style={{ width: 36 }} />
    </div>

    {/* Body */}
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {/* Activity bar */}
      <div style={{
        width: 44, background: '#333333',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '8px 0', gap: 14,
        borderRight: '1px solid #252526',
      }}>
        {['☰', '⌕', '⎇', '⊕'].map((s, i) => (
          <span key={i} style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>{s}</span>
        ))}
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          background: stage === 'success' ? 'rgba(193,61,255,0.2)' : 'transparent',
          border: stage === 'success' ? '1px solid var(--accent-line)' : '1px solid transparent',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: stage === 'success' ? 'var(--accent-2)' : 'rgba(255,255,255,0.5)',
        }}>
          <img src={sixthLogo} alt="" width={14} height={14} style={{ filter: stage === 'success' ? 'none' : 'grayscale(1) opacity(0.7)' }} />
        </div>
      </div>

      {/* Sidebar */}
      <div style={{
        width: 200, background: '#252526',
        borderRight: '1px solid #1e1e1e',
        padding: '12px 14px',
        fontSize: 11, color: 'rgba(255,255,255,0.7)',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'rgba(255,255,255,0.45)', marginBottom: 4,
        }}>SIXTH AI</div>
        {stage === 'success' ? (
          <>
            <div style={{
              padding: '8px 10px', borderRadius: 6,
              background: 'rgba(193,61,255,0.12)',
              border: '1px solid var(--accent-line)',
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--accent-2)',
              fontWeight: 500, fontSize: 10.5,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--success)', boxShadow: '0 0 0 3px rgba(111,224,168,0.2)' }} />
              Connected
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>SIGNED IN</div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email || 'you@company.com'}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Pro+ plan</div>
            <div style={{ height: 1, background: '#3d3d3d', margin: '6px 0' }} />
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>MODEL</div>
            <div style={{
              padding: '6px 8px', borderRadius: 4,
              background: 'rgba(255,255,255,0.04)',
              fontSize: 10.5, color: 'rgba(255,255,255,0.85)',
              display: 'flex', alignItems: 'center', gap: 6,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              <span style={{ color: 'var(--accent-2)' }}>◆</span>
              Claude Opus 4.7
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.45)' }}>Not signed in</div>
            <div style={{
              padding: '8px 10px', borderRadius: 6,
              background: 'rgba(255,255,255,0.04)',
              border: '1px dashed rgba(255,255,255,0.12)',
              fontSize: 10.5, color: 'rgba(255,255,255,0.55)',
              textAlign: 'center',
            }}>Waiting for auth…</div>
          </>
        )}
      </div>

      {/* Editor area */}
      <div style={{
        flex: 1, background: '#1e1e1e',
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        minWidth: 0,
      }}>
        <div style={{
          height: 32, background: '#252526',
          borderBottom: '1px solid #1e1e1e',
          display: 'flex', alignItems: 'stretch',
        }}>
          <div style={{
            padding: '0 14px',
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#1e1e1e',
            borderRight: '1px solid #252526',
            fontSize: 11, color: 'rgba(255,255,255,0.75)',
          }}>
            <span style={{ color: '#56b6c2' }}>{'{}'}</span>
            main.ts
          </div>
        </div>
        <div style={{
          padding: '14px 16px',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)', overflow: 'hidden',
        }}>
          {[
            ['1', 'import', ' { sixth } from "sixth";', '#9cdcfe'],
            ['2', '', '', ''],
            ['3', 'const', ' agent = sixth.agent({', '#9cdcfe'],
            ['4', '', '  model: "opus-4.7",', ''],
            ['5', '', '  mcp: ["github"],', ''],
            ['6', '', '});', ''],
            ['7', '', '', ''],
            ['8', 'await', ' agent.run("ship it");', '#c586c0'],
          ].map(([n, kw, rest, color]) => (
            <div key={n} style={{ display: 'flex', gap: 14, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              <span style={{ color: 'rgba(255,255,255,0.3)', minWidth: 16, textAlign: 'right', flexShrink: 0 }}>{n}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><span style={{ color }}>{kw}</span>{rest}</span>
            </div>
          ))}
        </div>

        {/* Authorize modal */}
        {stage === 'authorize' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 5,
          }}>
            <div style={{
              width: 'min(90%, 360px)',
              background: '#252526',
              border: '1px solid #3d3d3d',
              borderRadius: 8,
              padding: 18,
              boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
            }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600, marginBottom: 8 }}>
                Allow 'Sixth AI' extension to open this URI?
              </div>
              <div style={{
                padding: '8px 10px', borderRadius: 4,
                background: '#1e1e1e',
                border: '1px solid #3d3d3d',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10.5, color: 'rgba(255,255,255,0.6)',
                marginBottom: 14, wordBreak: 'break-all',
              }}>vscode://Sixth.sixth-ai/auth?apikey=••••&jwt=••••</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" style={{
                  padding: '5px 14px', borderRadius: 3, fontSize: 11, fontWeight: 500,
                  background: '#3d3d3d', color: 'rgba(255,255,255,0.85)',
                  border: 'none', fontFamily: 'inherit', cursor: 'default',
                }}>Cancel</button>
                <button type="button" style={{
                  padding: '5px 14px', borderRadius: 3, fontSize: 11, fontWeight: 600,
                  background: '#0e639c', color: 'white',
                  border: 'none', fontFamily: 'inherit', cursor: 'default',
                  boxShadow: '0 0 0 2px rgba(14,99,156,0.3)',
                  animation: 'sd-onb-vsPulse 2s ease-in-out infinite',
                }}>Open</button>
              </div>
            </div>
          </div>
        )}

        {/* Success toast */}
        {stage === 'success' && (
          <div style={{
            position: 'absolute', right: 16, bottom: 16,
            padding: '10px 14px',
            background: '#252526',
            border: '1px solid var(--accent-line)',
            borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 11.5, color: 'rgba(255,255,255,0.9)',
            boxShadow: '0 0 24px rgba(193,61,255,0.25)',
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: 999,
              background: 'rgba(111,224,168,0.18)', color: 'var(--success)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}><Check size={11} strokeWidth={3} /></span>
            Sixth AI connected
          </div>
        )}
      </div>
    </div>
    <style>{`
      @keyframes sd-onb-vsPulse {
        0%, 100% { box-shadow: 0 0 0 2px rgba(14,99,156,0.3); }
        50%      { box-shadow: 0 0 0 4px rgba(14,99,156,0.2); }
      }
    `}</style>
  </div>
);

/* ───────────── The page itself ───────────── */

const VSCodeOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // === Auth/URI logic preserved exactly ===
  const urlParams = new URLSearchParams(location.search);
  const vsCodeApiKey = urlParams.get('apikey') || localStorage.getItem('api_key');
  const vsCodeEmail = urlParams.get('email') || localStorage.getItem('user_email');
  const vsCodeJwt = urlParams.get('jwt') || localStorage.getItem('vscode_jwt_token');

  const vsCodeUri = `vscode://Sixth.sixth-ai/auth?apikey=${vsCodeApiKey}&email=${vsCodeEmail}&jwt=${vsCodeJwt}`;

  const handleOpenVSCode = () => {
    window.location.href = vsCodeUri;
    setTimeout(() => {
      setCompletedSteps((prev) => new Set(prev).add(1));
      setCurrentStep(2);
    }, 2000);
  };

  const handleContinueToDashboard = () => {
    localStorage.removeItem('vscode_referrer');
    localStorage.removeItem('vscode_callback_url');
    navigate('/vscode/dashboard');
  };

  const markStepComplete = (stepNumber) => {
    setCompletedSteps((prev) => new Set(prev).add(stepNumber));
    if (stepNumber < 3) setCurrentStep(stepNumber + 1);
  };

  const handlePrimary = () => {
    if (currentStep === 1) {
      handleOpenVSCode();
    } else if (currentStep === 2) {
      markStepComplete(2);
    } else {
      setCompletedSteps((prev) => new Set(prev).add(3));
      handleContinueToDashboard();
    }
  };

  const current = STEPS[currentStep - 1];

  return (
    <div className="sixth-design sd-page-enter" style={{ minHeight: '100vh' }}>
      {/* Top mini-nav */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40,
        borderBottom: '1px solid var(--line)',
        background: 'rgba(8,8,10,0.7)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '0 28px', height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, color: 'var(--text)' }}>
            <img src={sixthLogo} alt="Sixth AI" width={26} height={26} />
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.02em' }}>Sixth</span>
          </Link>
          <button
            type="button"
            onClick={handleContinueToDashboard}
            className="sd-btn sd-btn-ghost"
            style={{ fontSize: 13.5, color: 'var(--text-2)' }}
          >
            Skip to dashboard
          </button>
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 28px 60px' }}>
        {/* Header with progress bar */}
        <div style={{ marginBottom: 32 }}>
          <div className="sd-onb-head" style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            marginBottom: 18, flexWrap: 'wrap', gap: 12,
          }}>
            <div>
              <div style={{
                fontSize: 11, color: 'var(--accent-2)',
                textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 8,
              }}>
                Connecting Sixth to your editor
              </div>
              <h1 style={{
                fontSize: 'clamp(30px, 4vw, 52px)',
                fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1.0,
                margin: 0, color: 'var(--text)',
              }}>
                Almost there. <span className="sd-serif" style={{ color: 'var(--text-2)', fontWeight: 400 }}>Three steps.</span>
              </h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {STEPS.map((s, i) => {
                const done = completedSteps.has(s.id);
                const active = currentStep === s.id;
                return (
                  <React.Fragment key={s.id}>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(s.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 9,
                        padding: '7px 14px 7px 7px',
                        borderRadius: 999,
                        border: active ? '1px solid var(--accent-line)' : '1px solid var(--line)',
                        background: active ? 'rgba(193,61,255,0.06)' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'all 0.2s',
                        color: 'inherit',
                      }}
                    >
                      <span style={{
                        width: 24, height: 24, borderRadius: 999,
                        background: done ? 'var(--success)' : active ? 'var(--accent)' : 'rgba(255,255,255,0.05)',
                        color: done || active ? 'white' : 'var(--text-3)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace",
                        transition: 'all 0.2s',
                      }}>
                        {done ? <Check size={11} strokeWidth={3.5} /> : s.id}
                      </span>
                      <span style={{
                        fontSize: 12.5,
                        color: active ? 'var(--text)' : done ? 'var(--text-2)' : 'var(--text-3)',
                        fontWeight: active ? 500 : 400,
                        whiteSpace: 'nowrap',
                      }}>
                        {s.title}
                      </span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <div style={{
                        width: 28, height: 1,
                        background: completedSteps.has(s.id) ? 'var(--success)' : 'var(--line)',
                        transition: 'background 0.3s',
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Progress line */}
          <div style={{
            height: 2,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 999,
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${(currentStep / 3) * 100}%`,
              background: 'linear-gradient(90deg, var(--accent), var(--accent-2))',
              borderRadius: 999,
              transition: 'width 0.5s cubic-bezier(0.34, 1.2, 0.4, 1)',
            }} />
          </div>
        </div>

        {/* Main split */}
        <div className="sd-onb-split" style={{
          display: 'grid',
          gridTemplateColumns: '0.95fr 1.5fr',
          gap: 28,
          alignItems: 'stretch',
          minHeight: 540,
        }}>
          {/* Left — text card */}
          <div style={{
            padding: '40px 36px',
            borderRadius: 22,
            border: '1px solid var(--line)',
            background: 'var(--bg-card)',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              height: 1,
              background: 'linear-gradient(90deg, transparent, var(--accent), transparent)',
            }} />
            <div>
              <div className="sd-mono" style={{
                fontSize: 13, color: 'var(--accent-2)',
                textTransform: 'uppercase', letterSpacing: '0.16em',
                marginBottom: 16,
              }}>{current.eyebrow}</div>

              <div key={`title-${currentStep}`} className="sd-page-enter" style={{
                fontSize: 'clamp(40px, 5.5vw, 76px)',
                fontWeight: 500, letterSpacing: '-0.045em', lineHeight: 0.96,
                margin: '0 0 24px',
                color: 'var(--text)',
              }}>
                {current.title}<span className="sd-serif" style={{ color: 'var(--accent-2)', fontWeight: 400 }}>.</span>
              </div>
              <p key={`desc-${currentStep}`} className="sd-page-enter" style={{
                fontSize: 16, color: 'var(--text-2)', lineHeight: 1.6,
                margin: '0 0 24px',
              }}>{current.description}</p>

              {current.note && (
                <div key={`note-${currentStep}`} className="sd-page-enter" style={{
                  padding: 16,
                  borderRadius: 12,
                  background: 'rgba(255,180,84,0.04)',
                  border: '1px solid rgba(255,180,84,0.18)',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  marginBottom: 20,
                }}>
                  <span style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 1, fontSize: 14, fontWeight: 700 }}>!</span>
                  <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{current.note}</span>
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handlePrimary}
                  className="sd-btn sd-btn-accent"
                  style={{ height: 48, padding: '0 22px', fontSize: 14.5 }}
                >
                  {current.cta} <ArrowRight size={14} />
                </button>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="sd-btn sd-btn-ghost"
                    style={{ fontSize: 13 }}
                  >
                    ← Back
                  </button>
                )}
                {currentStep === 1 && (
                  <button
                    type="button"
                    onClick={() => { setCompletedSteps((prev) => new Set(prev).add(1)); setCurrentStep(2); }}
                    className="sd-btn sd-btn-ghost"
                    style={{ fontSize: 13, color: 'var(--text-3)' }}
                  >
                    Done it already
                  </button>
                )}
              </div>

              <div style={{
                marginTop: 28, paddingTop: 22,
                borderTop: '1px solid var(--line)',
                fontSize: 12, color: 'var(--text-3)',
                display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center',
              }}>
                <span>Stuck?</span>
                <a href="mailto:ope@trysixth.com" style={{ color: 'var(--accent-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>ope@trysixth.com</a>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <Link to="/vscode/dashboard" style={{ color: 'var(--text-2)' }}>Go to dashboard</Link>
              </div>
            </div>
          </div>

          {/* Right — mock visual */}
          <div style={{
            position: 'relative',
            borderRadius: 22,
            border: '1px solid var(--line)',
            background: 'linear-gradient(180deg, rgba(193,61,255,0.04), transparent 60%), #0d0d12',
            padding: 28,
            display: 'flex',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 18, left: 22,
              fontSize: 10, color: 'var(--text-3)',
              textTransform: 'uppercase', letterSpacing: '0.16em',
              zIndex: 5,
            }}>
              {currentStep === 1 ? "What you'll see in the browser"
                : currentStep === 2 ? "What you'll see in VS Code"
                : 'When connection succeeds'}
            </div>

            <div key={`mock-${currentStep}`} className="sd-page-enter" style={{
              marginTop: 18, flex: 1, display: 'flex', minHeight: 0,
            }}>
              {currentStep === 1 && <BrowserPopup />}
              {currentStep === 2 && <VSCodeWindow stage="authorize" email={vsCodeEmail} />}
              {currentStep === 3 && <VSCodeWindow stage="success" email={vsCodeEmail} />}
            </div>
          </div>
        </div>

        {/* Success-only secondary cards */}
        {currentStep === 3 && (
          <div className="sd-onb-success-grid sd-page-enter" style={{
            marginTop: 24,
            display: 'grid', gridTemplateColumns: '1fr', gap: 14,
          }}>
            {[
              { Icon: BookOpen, title: 'Read the docs', desc: 'Skills, MCP, hooks — everything you can plug into.' },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                style={{
                  padding: 20,
                  borderRadius: 14,
                  border: '1px solid var(--line)',
                  background: 'var(--bg-card)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent-line)'; e.currentTarget.style.background = 'rgba(193,61,255,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(193,61,255,0.12)',
                    color: 'var(--accent-2)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={14} />
                  </span>
                  <ArrowRight size={14} color="var(--text-3)" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>{title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.45 }}>{desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 980px) {
          .sd-onb-split { grid-template-columns: 1fr !important; }
          .sd-onb-success-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default VSCodeOnboarding;
